import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Alert,
  Drawer,
  StepContent,
  Card,
  CardContent,
  Stack,
  Collapse,
  LinearProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ChatIcon from '@mui/icons-material/Chat';
import TimelineIcon from '@mui/icons-material/Timeline';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import MainLayout from '../../layout/MainLayout';

// Import mock data
import dataRequestsData from '../../data/dataRequests.json';
import cohortsData from '../../data/cohorts.json';
import parameterListsData from '../../data/parameterLists.json';
import parametersData from '../../data/parameters.json';

const Requests = () => {
  const navigate = useNavigate();
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [requests, setRequests] = useState(dataRequestsData.requests);
  const [expandedRequestId, setExpandedRequestId] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState({});

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [requestType, setRequestType] = useState('cohort_creation');
  const [selectedParameterList, setSelectedParameterList] = useState(null);
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [requestNotes, setRequestNotes] = useState('');
  const [wizardSuccess, setWizardSuccess] = useState(false);

  const getStatusColor = (status) => {
    const statusColors = {
      'pending_approval': 'warning',
      'in_progress': 'info',
      'in_review': 'info',
      'completed': 'success',
      'rejected': 'error'
    };
    return statusColors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'pending_approval': 'Genehmigung ausstehend',
      'in_progress': 'In Bearbeitung',
      'in_review': 'In Prüfung',
      'completed': 'Abgeschlossen',
      'rejected': 'Abgelehnt',
      'feasibility': 'Machbarkeitsprüfung'
    };
    return statusLabels[status] || status;
  };

  const handleToggleRow = (requestId) => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId(null);
    } else {
      setExpandedRequestId(requestId);
      // Initialize chat messages for this request if not exists
      if (!chatMessages[requestId]) {
        setChatMessages({
          ...chatMessages,
          [requestId]: [
            {
              id: 1,
              sender: 'Data Steward Team',
              message: 'Ihre Anfrage wird geprüft.',
              timestamp: '2024-03-15 14:30',
              isSystem: true
            }
          ]
        });
      }
    }
  };

  const handleCreateNew = () => {
    setShowCreateWizard(true);
    setActiveStep(0);
    setWizardSuccess(false);
    // Reset form
    setSelectedCohort(null);
    setRequestType('cohort_creation');
    setSelectedParameterList(null);
    setSelectedParameters([]);
    setRequestNotes('');
  };

  const handleCloseWizard = () => {
    setShowCreateWizard(false);
    setActiveStep(0);
    setWizardSuccess(false);
  };

  const handleSendMessage = (requestId) => {
    const currentMessages = chatMessages[requestId] || [];
    if (chatMessage.trim()) {
      setChatMessages({
        ...chatMessages,
        [requestId]: [
          ...currentMessages,
          {
            id: currentMessages.length + 1,
            sender: 'Sie',
            message: chatMessage,
            timestamp: new Date().toLocaleString('de-DE'),
            isSystem: false
          }
        ]
      });
      setChatMessage('');
    }
  };

  const handleParameterListChange = (event) => {
    const listId = event.target.value;
    const list = parameterListsData.lists.find(l => l.id === listId);
    setSelectedParameterList(list);

    if (list) {
      const parameters = parametersData.parameters.filter(param =>
        typeof param.id === 'number' && list.parameterIds.includes(param.id)
      );
      setSelectedParameters(parameters);
    } else {
      setSelectedParameters([]);
    }
  };

  // Wizard navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmitRequest = () => {
    // Generate request ID
    const requestId = `REQ-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;

    const newRequest = {
      id: requestId,
      type: requestType === 'cohort_creation' ? 'feasibility' : 'data_provision',
      cohortName: selectedCohort.name,
      cohortId: selectedCohort.id,
      status: 'pending_approval',
      requestedAt: new Date().toISOString(),
      requestedBy: 'Aktueller Benutzer',
      notes: requestNotes,
      variableCount: requestType === 'data_extraction' ? selectedParameters.length : undefined,
      variables: requestType === 'data_extraction' ? selectedParameters.map(p => ({
        id: p.id,
        name: p.name
      })) : undefined,
      parameterListName: selectedParameterList?.name,
      estimatedPatients: selectedCohort.patientCount
    };

    setRequests([newRequest, ...requests]);
    setWizardSuccess(true);
    setActiveStep(activeStep + 1);

    // Auto-close after 3 seconds and expand the new request
    setTimeout(() => {
      setShowCreateWizard(false);
      setActiveStep(0);
      setWizardSuccess(false);
      setExpandedRequestId(requestId);
      // Initialize chat for new request
      setChatMessages({
        ...chatMessages,
        [requestId]: [
          {
            id: 1,
            sender: 'Data Steward Team',
            message: 'Ihre Anfrage wird geprüft.',
            timestamp: new Date().toLocaleString('de-DE'),
            isSystem: true
          }
        ]
      });
    }, 3000);
  };

  const getTimelineSteps = (request) => {
    const steps = [
      {
        label: 'Anfrage eingereicht',
        date: request.requestedAt,
        completed: true,
        description: `Von ${request.requestedBy} eingereicht`,
        details: `Anfrage-ID: ${request.id}`
      },
      {
        label: 'Prüfung durch Data Steward Team',
        date: request.approvedAt || null,
        completed: !!request.approvedAt,
        description: request.approvedAt ? 'Anfrage wurde geprüft und zur Bearbeitung freigegeben' : 'Anfrage wird durch das Data Steward Team geprüft',
        details: request.approvedAt ? 'Rechtliche und technische Machbarkeit bestätigt' : null
      },
      {
        label: 'Genehmigt und zugewiesen',
        date: request.approvedAt,
        completed: !!request.approvedAt,
        description: request.approvedAt ? 'Anfrage wurde an das Datenmanagement-Team weitergeleitet' : null,
        details: request.approvedAt ? 'Zuständiger Bearbeiter wurde informiert' : null
      },
      {
        label: 'Datenextraktion in Bearbeitung',
        date: null,
        completed: request.status === 'completed',
        description: request.status === 'completed' ? 'Daten wurden erfolgreich extrahiert' : request.status === 'in_progress' ? 'Datenextraktion läuft' : null,
        details: request.status === 'completed' || request.status === 'in_progress' ? `${request.variableCount || 0} Parameter werden verarbeitet` : null
      },
      {
        label: 'Bereitgestellt',
        date: request.completedAt,
        completed: request.status === 'completed',
        description: request.status === 'completed' ? 'Daten stehen zum Download bereit' : null,
        details: request.status === 'completed' ? 'Export wurde erfolgreich generiert' : null
      },
    ];
    return steps;
  };

  // Wizard steps
  const wizardSteps = [
    'Kohorte auswählen',
    'Anfrageart wählen',
    requestType === 'data_extraction' ? 'Parameter auswählen' : 'Notizen',
    requestType === 'data_extraction' ? 'Notizen' : null,
    'Zusammenfassung'
  ].filter(Boolean);

  // Check if current step is valid
  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return !!selectedCohort;
      case 1:
        return !!requestType;
      case 2:
        if (requestType === 'data_extraction') {
          return selectedParameters.length > 0;
        }
        return true;
      case 3:
        return true;
      case 4:
        // Summary step - always valid if we got here
        return true;
      default:
        return false;
    }
  };

  const renderWizardStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Wählen Sie die Kohorte, für die Sie eine Anfrage erstellen möchten.
            </Alert>
            <FormControl fullWidth error={!selectedCohort && activeStep > 0}>
              <InputLabel>Kohorte auswählen*</InputLabel>
              <Select
                value={selectedCohort?.id || ''}
                label="Kohorte auswählen*"
                onChange={(e) => {
                  const cohort = cohortsData.cohorts.find(c => c.id === e.target.value);
                  setSelectedCohort(cohort);
                }}
              >
                {cohortsData.cohorts.map((cohort) => (
                  <MenuItem key={cohort.id} value={cohort.id}>
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body1" fontWeight="medium">{cohort.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cohort.patientCount.toLocaleString('de-DE')} Patienten • {cohort.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedCohort && (
              <Card variant="outlined" sx={{ mt: 2, bgcolor: 'success.50', borderColor: 'success.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2" fontWeight="bold" color="success.dark">
                      Kohorte ausgewählt
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {selectedCohort.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedCohort.patientCount.toLocaleString('de-DE')} Patienten
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Wählen Sie die Art der Anfrage aus.
            </Alert>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={requestType}
                onChange={(e) => setRequestType(e.target.value)}
              >
                <Card
                  variant="outlined"
                  sx={{
                    mb: 2,
                    border: requestType === 'cohort_creation' ? 2 : 1,
                    borderColor: requestType === 'cohort_creation' ? 'primary.main' : 'divider',
                    bgcolor: requestType === 'cohort_creation' ? 'primary.50' : 'inherit',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: requestType === 'cohort_creation' ? 'primary.50' : 'action.hover', transform: 'translateY(-2px)', boxShadow: 2 }
                  }}
                  onClick={() => setRequestType('cohort_creation')}
                >
                  <CardContent>
                    <FormControlLabel
                      value="cohort_creation"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            Kohortenerstellung / Fallzahlermittlung
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Beantragen Sie die Erstellung der Kohorte und erhalten Sie die Anzahl der Fälle
                          </Typography>
                        </Box>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </CardContent>
                </Card>
                <Card
                  variant="outlined"
                  sx={{
                    border: requestType === 'data_extraction' ? 2 : 1,
                    borderColor: requestType === 'data_extraction' ? 'primary.main' : 'divider',
                    bgcolor: requestType === 'data_extraction' ? 'primary.50' : 'inherit',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: requestType === 'data_extraction' ? 'primary.50' : 'action.hover', transform: 'translateY(-2px)', boxShadow: 2 }
                  }}
                  onClick={() => setRequestType('data_extraction')}
                >
                  <CardContent>
                    <FormControlLabel
                      value="data_extraction"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            Datenextraktion
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Beantragen Sie die Extraktion von Parametern für die Fallnummern der Kohorte
                          </Typography>
                        </Box>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </CardContent>
                </Card>
              </RadioGroup>
            </FormControl>
          </Box>
        );
      case 2:
        if (requestType === 'data_extraction') {
          return (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Wählen Sie eine Parameterliste und die zu extrahierenden Parameter aus.
              </Alert>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Parameterliste auswählen*</InputLabel>
                <Select
                  value={selectedParameterList?.id || ''}
                  label="Parameterliste auswählen*"
                  onChange={handleParameterListChange}
                >
                  {parameterListsData.lists.map((list) => (
                    <MenuItem key={list.id} value={list.id}>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">{list.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {list.parameterIds.length} verfügbare Parameter
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedParameterList && (
                <>
                  <Autocomplete
                    multiple
                    options={parametersData.parameters.filter(param =>
                      typeof param.id === 'number' && selectedParameterList.parameterIds.includes(param.id)
                    )}
                    getOptionLabel={(option) => option.name}
                    value={selectedParameters}
                    onChange={(event, newValue) => {
                      setSelectedParameters(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Parameter auswählen*"
                        placeholder="Beginnen Sie mit der Eingabe..."
                        error={selectedParameters.length === 0}
                        helperText={selectedParameters.length === 0 ? "Bitte wählen Sie mindestens einen Parameter aus" : ""}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          size="small"
                          color="primary"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                  {selectedParameters.length > 0 && (
                    <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedParameters.length} Parameter ausgewählt
                      </Typography>
                    </Alert>
                  )}
                </>
              )}

              {!selectedParameterList && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Bitte wählen Sie zuerst eine Parameterliste aus.
                </Alert>
              )}
            </Box>
          );
        }
        // Fallthrough for cohort_creation
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Fügen Sie optionale Notizen oder zusätzliche Informationen hinzu.
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Zusätzliche Informationen oder Anmerkungen..."
              value={requestNotes}
              onChange={(e) => setRequestNotes(e.target.value)}
              helperText="Optional: Beschreiben Sie besondere Anforderungen oder Details"
            />
          </Box>
        );
      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Fügen Sie optionale Notizen oder zusätzliche Informationen hinzu.
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Zusätzliche Informationen oder Anmerkungen..."
              value={requestNotes}
              onChange={(e) => setRequestNotes(e.target.value)}
              helperText="Optional: Beschreiben Sie besondere Anforderungen oder Details"
            />
          </Box>
        );
      case 4:
        // Summary step
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Überprüfen Sie Ihre Anfrage vor dem Absenden.
            </Typography>
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Ausgewählte Kohorte
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedCohort?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCohort?.patientCount} Patienten
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Anfrageart
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {requestType === 'cohort_creation'
                      ? 'Kohortenerstellung / Fallzahlermittlung'
                      : 'Datenextraktion'}
                  </Typography>
                </CardContent>
              </Card>

              {requestType === 'data_extraction' && selectedParameters.length > 0 && (
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Ausgewählte Parameter
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {selectedParameterList?.name} - {selectedParameters.length} Parameter
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {selectedParameters.slice(0, 10).map((param) => (
                        <Chip key={param.id} label={param.name} size="small" />
                      ))}
                      {selectedParameters.length > 10 && (
                        <Chip
                          label={`+${selectedParameters.length - 10} weitere`}
                          size="small"
                          color="primary"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {requestNotes && (
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Notizen
                    </Typography>
                    <Typography variant="body2">
                      {requestNotes}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              <Alert severity="info" icon={<DescriptionIcon />}>
                Die Anfrage wird zur Prüfung an das Data Steward Team weitergeleitet.
              </Alert>
            </Stack>
          </Box>
        );
      default:
        return null;
    }
  };

  // Render create wizard
  const renderCreateWizard = () => {
    const progressPercentage = ((activeStep + 1) / wizardSteps.length) * 100;

    return (
      <Drawer
        anchor="right"
        open={showCreateWizard}
        onClose={handleCloseWizard}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 600 } }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" fontWeight="bold">
                Neue Anfrage erstellen
              </Typography>
              <IconButton onClick={handleCloseWizard} size="large">
                <CloseIcon />
              </IconButton>
            </Box>
            {!wizardSuccess && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Schritt {activeStep + 1} von {wizardSteps.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight="bold">
                    {Math.round(progressPercentage)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{ height: 6, borderRadius: 1 }}
                />
              </>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {wizardSuccess ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Anfrage erfolgreich erstellt!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Die Anfrage wird geöffnet...
                </Typography>
              </Box>
            ) : (
              <Stepper activeStep={activeStep} orientation="vertical">
                {wizardSteps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel>
                      <Typography variant="body1" fontWeight="bold">
                        {label}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {renderWizardStepContent(index)}
                      <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                        <Button
                          disabled={index === 0}
                          onClick={handleBack}
                          startIcon={<NavigateBeforeIcon />}
                          size="large"
                        >
                          Zurück
                        </Button>
                        {index === wizardSteps.length - 1 ? (
                          <Button
                            variant="contained"
                            onClick={handleSubmitRequest}
                            disabled={!isStepValid()}
                            startIcon={<SendIcon />}
                            size="large"
                          >
                            Anfrage erstellen
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!isStepValid()}
                            endIcon={<NavigateNextIcon />}
                            size="large"
                          >
                            Weiter
                          </Button>
                        )}
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            )}
          </Box>
        </Box>
      </Drawer>
    );
  };

  // Render expanded row content
  const ExpandedRowContent = ({ request }) => {
    const [tabValue, setTabValue] = useState(0);
    const requestMessages = chatMessages[request.id] || [];

    return (
      <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {request.cohortName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={getStatusLabel(request.status)}
              color={getStatusColor(request.status)}
              size="medium"
            />
            <IconButton size="small" onClick={() => setExpandedRequestId(null)}>
              <KeyboardArrowUpIcon />
            </IconButton>
          </Box>
        </Box>

        <Paper sx={{ bgcolor: 'white' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<DescriptionIcon />} label="Übersicht" iconPosition="start" />
            <Tab icon={<TimelineIcon />} label="Timeline" iconPosition="start" />
            <Tab icon={<ChatIcon />} label="Chat" iconPosition="start" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Tab 0: Übersicht */}
            {tabValue === 0 && (
              <Stack spacing={2}>
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Anfrage-ID</Typography>
                        <Typography variant="body2" fontWeight="600">{request.id}</Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Typ</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {request.type === 'feasibility' ? 'Machbarkeitsprüfung' : 'Datenbereitstellung'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Angefragt von</Typography>
                        <Typography variant="body2" fontWeight="600">{request.requestedBy}</Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Angefragt am</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {new Date(request.requestedAt).toLocaleDateString('de-DE', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                      {request.variableCount && (
                        <>
                          <Divider />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Parameter</Typography>
                            <Typography variant="body2" fontWeight="600">{request.variableCount}</Typography>
                          </Box>
                        </>
                      )}
                      {request.estimatedPatients && (
                        <>
                          <Divider />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Geschätzte Fallzahl</Typography>
                            <Typography variant="body2" fontWeight="600">{request.estimatedPatients.toLocaleString('de-DE')} Patienten</Typography>
                          </Box>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                {request.notes && (
                  <Alert severity="info" icon={<DescriptionIcon />}>
                    <Typography variant="body2">{request.notes}</Typography>
                  </Alert>
                )}

                {request.variables && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Ausgewählte Parameter ({request.variables.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {request.variables.map((variable) => (
                        <Chip key={variable.id} label={variable.name} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            )}

            {/* Tab 1: Timeline */}
            {tabValue === 1 && (
              <Box>
                <Stepper orientation="vertical">
                  {getTimelineSteps(request).map((step, index) => (
                    <Step key={index} active={step.completed} completed={step.completed}>
                      <StepLabel
                        StepIconProps={{
                          sx: {
                            fontSize: '1.75rem',
                            '&.Mui-completed': { color: 'success.main' },
                            '&.Mui-active': { color: 'primary.main' }
                          }
                        }}
                      >
                        <Typography variant="body1" fontWeight={step.completed ? '600' : 'normal'}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Box sx={{ ml: 0, pb: 2 }}>
                          {step.date && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {new Date(step.date).toLocaleString('de-DE', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })} Uhr
                            </Typography>
                          )}
                          {step.description && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {step.description}
                            </Typography>
                          )}
                          {step.details && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                              {step.details}
                            </Typography>
                          )}
                          {!step.date && !step.completed && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                Ausstehend
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}

            {/* Tab 2: Chat */}
            {tabValue === 2 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Kommunikation
                </Typography>
                <Card variant="outlined" sx={{ mb: 2, maxHeight: 400, overflow: 'auto' }}>
                  <CardContent>
                    {requestMessages.map((msg, index) => (
                      <Box key={msg.id} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: msg.isSystem ? 'primary.main' : 'secondary.main' }}>
                            {msg.sender[0]}
                          </Avatar>
                          <Typography variant="body2" fontWeight="bold">
                            {msg.sender}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {msg.timestamp}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ ml: 5 }}>
                          {msg.message}
                        </Typography>
                        {index < requestMessages.length - 1 && <Divider sx={{ mt: 2 }} />}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Nachricht eingeben..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(request.id)}
                  />
                  <Button
                    variant="contained"
                    onClick={() => handleSendMessage(request.id)}
                    disabled={!chatMessage.trim()}
                  >
                    <SendIcon />
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Anfragen
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Verwalten Sie Ihre Datenanfragen
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            size="large"
          >
            Neue Anfrage
          </Button>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 50 }} />
                <TableCell sx={{ fontWeight: 600 }}>Anfrage-ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Kohorte</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Angefragt am</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Noch keine Anfragen gestellt
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Erstellen Sie Ihre erste Anfrage mit dem Button oben
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <React.Fragment key={request.id}>
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: expandedRequestId === request.id ? 'action.selected' : 'inherit',
                        '&:hover': { bgcolor: expandedRequestId === request.id ? 'action.selected' : 'action.hover' }
                      }}
                      onClick={() => handleToggleRow(request.id)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {expandedRequestId === request.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {request.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.type === 'feasibility' ? 'Machbarkeit' : 'Datenbereitstellung'}
                          size="small"
                          variant="outlined"
                          color={request.type === 'feasibility' ? 'info' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{request.cohortName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(request.status)}
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {request.variableCount ? `${request.variableCount} Parameter` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(request.requestedAt).toLocaleDateString('de-DE')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                        <Collapse in={expandedRequestId === request.id} timeout="auto" unmountOnExit>
                          <ExpandedRowContent request={request} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Wizard Drawer */}
      {renderCreateWizard()}
    </MainLayout>
  );
};

export default Requests;
